// Copyright TraderEvolution Global LTD. © 2017-2024. All rights reserved.

import { Resources } from "../../Commons/properties/Resources.js"
import { OrderItem } from "../cache/OrderItem.js"
import { ColouringModes } from "../elements/QuickTable/QuickTableColumn.js"
import { QuickTableUtils } from "../elements/QuickTable/QuickTableUtils.js"
import { TradingButtonItem } from "../elements/TradingButtonStripe.js"
import { PanelNames } from "../UtilsClasses/FactoryConstants.js"
import { ApplicationPanelNew } from "./ApplicationPanelNew.js";
import { OrdersPanelBase } from "./OrdersPanelBase.js"
import { ThemeManager } from "../misc/ThemeManager.js"
import { PlacedFrom } from "../../Utils/Trading/PlacedFrom.js"
import { IsAllowed } from "../../Commons/IsAllowed.js"
import { OrderActionEnum } from "../../Utils/Trading/OrderActionEnum.js"
import { NumericLinks } from "../../Commons/cache/OrderParams/order-edit/OrderEditBase.js"
import { DataCache } from "../../Commons/DataCache.js"
import { SessionSettings } from "../../Commons/SessionSettings.js"

export let OrdersPanel = OrdersPanelBase.extend(
    {
        Name: 'OrdersPanel',
        topPanelHeight: 25,
        data: function ()
        {
            return {
                isAccountLinkShow: true,
                isSymbolLinkShow: true
            }
        },
        headerLocaleKey: 'panel.orders',
        moreThanOneTTKey: 'panel.orders.menu.MoreOneSymbolInfoDisabled.tt',     // #95439
        noOneTTKey: 'panel.orders.menu.NoSymbolInfoDisabled.tt'
    })

OrdersPanel.prototype.getType = function ()
{
    return PanelNames.OrdersPanel
}

OrdersPanel.prototype.oncomplete = function ()
{
    OrdersPanelBase.prototype.oncomplete.call(this)

    let qt = this.quickTableRactive.quickTable
    if (!qt) return

    qt.onTableMouseDown.Subscribe(this.onTableMouseDown, this);
}

OrdersPanel.prototype.onTableMouseDown = function (hittestInfo)
{
    let timerID = this.doubleClickedTimerID
    if (timerID)
        clearTimeout(timerID)

    this.doubleClickedTimerID = setTimeout(function () { this.processTableMouseDown(hittestInfo) }.bind(this), 200)
}

OrdersPanel.prototype.processTableMouseDown = function (hittestInfo)
{
    let qt = this.quickTableRactive.quickTable

    if (!qt || qt.editableColID != hittestInfo.columnIndex || qt.editableRowID != hittestInfo.rowIndex)
        return

    let actions = OrderActionEnum
    let placedFromVal = PlacedFrom.WEB_ORDERS_PANEL_DB_CLICK
    let columnID = qt.sortedColumns[hittestInfo.columnIndex].PRIVATE.index
    let numericLinkName, isSLTP = false

    let item = qt.rowsArray[hittestInfo.rowIndex].item,
        order = item.FOrder
    if (item)
        isSLTP = order.isClosingOrder() && (order.SLOrder !== null || order.TPOrder !== null)      // #88431 isSLTP != order.isClosingOrder() для частично зафиленных ордеров, добавил доп проверку

    if (columnID == OrderItem.QUANTITY_COL_INDEX)
        numericLinkName = NumericLinks.Quantity
    else if (columnID == OrderItem.PRICE_COL_INDEX)
        numericLinkName = isSLTP ? NumericLinks.TakeProfit : NumericLinks.LimitPrice
    else if (columnID == OrderItem.STOP_PRICE_COL_INDEX)
        numericLinkName = isSLTP ? NumericLinks.StopLoss : NumericLinks.StopPrice
    else if (columnID == OrderItem.SL_PRICE_COL_INDEX || columnID == OrderItem.SL_OFFSET_COL_INDEX)
        numericLinkName = NumericLinks.StopLoss
    else if (columnID == OrderItem.TP_PRICE_COL_INDEX || columnID == OrderItem.TP_OFFSET_COL_INDEX)
        numericLinkName = NumericLinks.TakeProfit

    this.action(actions.ModifyOrder, true, placedFromVal, numericLinkName)
}

OrdersPanel.prototype.getDoubleClickActionParams = function ()
{
    return {
        action: OrderActionEnum.ByIds,
        confirm: true,
        placedFrom: PlacedFrom.WEB_ORDERS_PANEL_DB_CLICK
    }
}

OrdersPanel.prototype.getHotButtonPlacedFrom = function ()
{
    return PlacedFrom.WEB_ORDERS_PANEL_HOT_BTN
}

// TODO. Ugly.
OrdersPanel.prototype.onDrawCell = function (gr, e)
{
    let columnIndex = e.columnIndex
    if (columnIndex !== 41 && columnIndex !== 43)
        return

    if (!e.tableItem.isTrStopSlOrder)
        return

    OrdersPanel.drawTrStopCell(gr, e)
}

OrdersPanel.drawTrStopCell = function (gr, e)
{
    let left = Math.round(e.x)
    let cellY = e.y
    let cellW = e.width

    let imgW = 16

    if (cellW > imgW)
    {
        gr.drawImage(ThemeManager.CurrentTheme.trStopDisabledImage, left + 0.5, cellY + 1)
        left += imgW
    }

    gr.fillStyle = e.ForeColor
    QuickTableUtils.DrawTextInRect(gr, e.cell.formattedValue, left, cellY, cellW, e.height, 5, 3)

    e.handled = true
}

OrdersPanel.prototype.getFullInstrumentName = function (rowItem)
{
    return rowItem.InstrumentName()
}

OrdersPanel.prototype.CloseBtnClick = function (data)
{
    let selectedOrderIdArr = [data.row.id]

    if (this.IsClosingForbidden(selectedOrderIdArr))
        return

    DataCache.FOrderExecutor.orderAction(OrderActionEnum.ByIds, selectedOrderIdArr, true, PlacedFrom.WEB_ORDERS_PANEL_DB_CLICK);
}

OrdersPanel.prototype.IsClosingAllowed = function (selectedOrderIdArr)
{
    const orders = DataCache.getOrdersById(selectedOrderIdArr);
    let result = null;
    for (const ID in orders) {
        const res = IsAllowed.IsOrderCancelingAllowed(orders[ID]);
        if (result === null || res.Allowed === false)
            result = res;
    }
    return result;
}

OrdersPanel.prototype.getAccount = function (rowItem)
{
    return rowItem.GetCurrentAccount().userID
}

OrdersPanel.prototype.DCEventsSubscribe = function ()
{
    DataCache.OnAddOrder.Subscribe(this.AddOrderEvent, this)
    DataCache.OnRemoveOrder.Subscribe(this.RemoveOrderEvent, this)

    DataCache.OnAddSLOrderToPosition.Subscribe(this.AddSLOrderToPositionEvent, this)
    DataCache.OnAddTPOrderToPosition.Subscribe(this.AddTPOrderToPositionEvent, this)

    DataCache.OnRemoveSLOrderFromPosition.Subscribe(this.RemoveSLOrderFromPositionEvent, this)
    DataCache.OnRemoveTPOrderFromPosition.Subscribe(this.RemoveTPOrderFromPositionEvent, this)
}

OrdersPanel.prototype.DCEventsUnSubscribe = function ()
{
    DataCache.OnAddOrder.UnSubscribe(this.AddOrderEvent, this)
    DataCache.OnRemoveOrder.UnSubscribe(this.RemoveOrderEvent, this)

    DataCache.OnAddSLOrderToPosition.UnSubscribe(this.AddSLOrderToPositionEvent, this)
    DataCache.OnAddTPOrderToPosition.UnSubscribe(this.AddTPOrderToPositionEvent, this)

    DataCache.OnRemoveSLOrderFromPosition.UnSubscribe(this.RemoveSLOrderFromPositionEvent, this)
    DataCache.OnRemoveTPOrderFromPosition.UnSubscribe(this.RemoveTPOrderFromPositionEvent, this)
}

OrdersPanel.prototype.AddOrderEvent = function (order)
{
    let row = this.quickTableRactive.quickTable.AddItem(new OrderItem(order, SessionSettings))
}

OrdersPanel.prototype.AddSLOrderToPositionEvent = function (position)
{
    if (position && position.SLOrder)
        this.AddOrderEvent(position.SLOrder)
}

OrdersPanel.prototype.AddTPOrderToPositionEvent = function (position)
{
    if (position && position.TPOrder)
        this.AddOrderEvent(position.TPOrder)
}

OrdersPanel.prototype.RemoveOrderEvent = function (order)
{
    this.quickTableRactive.quickTable.RemoveItem(
        OrderItem.GetItemId(order))
}

OrdersPanel.prototype.RemoveSLOrderFromPositionEvent = function (position)
{
    if (position && position.SLOrder)
        this.RemoveOrderEvent(position.SLOrder)
}

OrdersPanel.prototype.RemoveTPOrderFromPositionEvent = function (position)
{
    if (position && position.TPOrder)
        this.RemoveOrderEvent(position.TPOrder)
}

OrdersPanel.prototype.populateItemsDirect = function ()
{
    OrdersPanelBase.prototype.populateItemsDirect.call(this)
    let orderDict = DataCache.getAllOrders()
    for (let id in orderDict)
        this.AddOrderEvent(orderDict[id])
}

OrdersPanel.prototype.createMenuItems = function ()
{
    let actions = OrderActionEnum
    let placedFromVal = PlacedFrom.WEB_ORDERS_PANEL_CONTEXT_MENU

    let items = [
        {
            locKey: 'panel.orders.menu.ModifyOrder',
            tag: actions.ModifyOrder,
            enabled: true,
            event: this.action.bind(this, actions.ModifyOrder, true, placedFromVal, null)
        },
        {
            locKey: 'panel.orders.menu.ExecuteAsMarket',
            tag: actions.ChangeToMarket,
            enabled: true,
            visible: !Resources.isHidden('panel.orders.menu.ExecuteAsMarket'),
            event: this.action.bind(this, actions.ChangeToMarket, true, placedFromVal)
        },
        {
            locKey: 'panel.orders.menu.Remove',
            tag: 'CloseGroup',
            enabled: true,
            subitems: [
                {
                    locKey: 'panel.orders.menu.CancelOrder',
                    tag: actions.ByIds,
                    enabled: true,
                    event: this.action.bind(this, actions.ByIds, true, placedFromVal)
                },
                {
                    locKey: 'panel.orders.menu.CancelAllByInstrument',
                    tag: actions.ByInstrument,
                    enabled: true,
                    event: this.action.bind(this, actions.ByInstrument, true, placedFromVal)
                },
                {
                    locKey: 'panel.orders.menu.CancelAllForAccount',
                    tag: actions.ByAccount,
                    enabled: true,
                    event: this.action.bind(this, actions.ByAccount, true, placedFromVal)
                },
                {
                    locKey: 'panel.orders.menu.CancelAllOrders',
                    tag: actions.All,
                    enabled: true,
                    event: this.action.bind(this, actions.All, true, placedFromVal)
                }
            ]
        }
    ]

    if (!Resources.isHidden('panel.orders.menu.ShowToolbar'))  // переделать в случае появления новых subitems
        items.push(
            {
                locKey: 'panel.positions.menu.View',
                tag: 'View',
                enabled: true,
                subitems: [
                    {
                        locKey: 'panel.orders.menu.ShowToolbar',
                        tag: "ShowToolbar",
                        checked: this.ToolbarVisible,
                        enabled: true,
                        canCheck: true,
                        event: this.ShowToolbarStateChange.bind(this)
                    }]
            })

    items = this.AddOpeningPanelsCM(items)
    this.AddSymbolInfoContextMenuItemIfNeed(items)

    return items
}

OrdersPanel.prototype.GetOpeningPanelsCMLocKeysSet = function ()
{
    let keysObj = {};

    keysObj[ApplicationPanelNew.OPEN_CHART] = 'panel.orders.showChartMI';
    keysObj[ApplicationPanelNew.OPEN_MD] = 'panel.orders.showMDMI';
    keysObj[ApplicationPanelNew.OPEN_TS] = 'panel.orders.showTSMI';
    keysObj[ApplicationPanelNew.OPEN_OE] = 'panel.orders.showOEMI';

    return keysObj;
}

OrdersPanel.prototype.createTradingButtonItems = function ()
{
    let actions = OrderActionEnum
    return [
        new TradingButtonItem(actions.All, 'panels.orders.buttons.CancelAllButton'),
        new TradingButtonItem(actions.Sell, 'panels.orders.buttons.CancelSellButton'),
        new TradingButtonItem(actions.Buy, 'panels.orders.buttons.CancelBuyButton'),
        new TradingButtonItem(actions.Stops, 'panels.orders.buttons.CancelStopsButton'),
        new TradingButtonItem(actions.Limits, 'panels.orders.buttons.CancelLimitsButton'),
        new TradingButtonItem(actions.Days, 'panels.orders.buttons.CancelDayButton'),
        new TradingButtonItem(actions.GTCs, 'panels.orders.buttons.CancelGTCButton'),
        new TradingButtonItem(actions.ByIds, 'panels.orders.buttons.CancelSelectedButton'),
        new TradingButtonItem(actions.ByAccountAndInstrument, 'panels.orders.buttons.CancelByInstrumentButton'),
        new TradingButtonItem(actions.ModifyOrder, 'panels.orders.buttons.ModifyOrder'),
        new TradingButtonItem(actions.ChangeToMarket, 'panels.orders.buttons.AsMarket')
    ]
}

OrdersPanel.prototype.updateAllowedActions = function ()
{
    if (!this.Controls || !this.quickTableRactive)
        return

    let menuTagDict = this.menuTagDict
    if (!menuTagDict) return

    let qt = this.quickTableRactive.quickTable
    if (!qt) return

    let btns = this.Controls.btns
    if (!btns) return

    let actions = OrderActionEnum

    let selectedOrderIdArr = qt.selectedRowIds
    let row = qt.rows[selectedOrderIdArr[0]]
    let allowedActionSet = DataCache.FOrderExecutor.getAllowedOrderActionSet(selectedOrderIdArr)
    let ins = row ? row.item.DisplayInstrumentName() : ''

    btns.updateEnability(allowedActionSet)
    btns.setInstrument(actions.ByAccountAndInstrument, ins)

    const changeToMarket = allowedActionSet[actions.ChangeToMarket];
    const modifyOrder = allowedActionSet[actions.ModifyOrder];
    const cancelAll = allowedActionSet[actions.All];
    const cancelByIds = allowedActionSet[actions.ByIds];
    const cancelByInstrument = allowedActionSet[actions.ByInstrument];
    const cancelByAccount = allowedActionSet[actions.ByAccount];

    menuTagDict[actions.ChangeToMarket].enabled = changeToMarket !== undefined && changeToMarket.Allowed;
    menuTagDict[actions.ModifyOrder].enabled = modifyOrder !== undefined && modifyOrder.Allowed;

    menuTagDict['CloseGroup'].enabled = cancelAll !== undefined || cancelByInstrument !== undefined || cancelByAccount !== undefined || cancelByIds !== undefined;

    menuTagDict[actions.All].enabled = cancelAll !== undefined && cancelAll.Allowed;
    menuTagDict[actions.ByIds].enabled = cancelByIds && cancelByIds.Allowed;
    menuTagDict[actions.ByInstrument].enabled = cancelByInstrument && cancelByInstrument.Allowed;
    menuTagDict[actions.ByAccount].enabled = cancelByAccount && cancelByAccount.Allowed;

    let enabled = !!ins;
    this.SetOpeningPanelsCMEnability(menuTagDict, enabled);
}

// TODO. Rename.
OrdersPanel.prototype.action = function (action, confirm, placedFrom, columnIndexToActivate)
{
    if (!OrdersPanelBase.prototype.action.call(this, action))
    {
        return;
    }

    DataCache.FOrderExecutor.orderAction(action, this.quickTableRactive.quickTable.selectedRowIds, confirm, placedFrom, columnIndexToActivate)
}

OrdersPanel.prototype.isClosingAction = function (action) 
{
    const closingActions = [
        OrderActionEnum.All,
        OrderActionEnum.ByIds,
        OrderActionEnum.Buy,
        OrderActionEnum.Sell,
        OrderActionEnum.ByInstrument,
        OrderActionEnum.ByAccount,
        OrderActionEnum.ByAccountAndInstrument
    ];

    return closingActions.includes(action);
}

OrdersPanel.prototype.SetColumnsDefaultDisplayIndex = function (table)
{
    table.columns[0].displayedIndex = 0;
    table.columns[1].displayedIndex = 1;
    table.columns[4].displayedIndex = 2;
    table.columns[11].displayedIndex = 3;
    table.columns[6].displayedIndex = 4;
    table.columns[7].displayedIndex = 5;
    table.columns[9].displayedIndex = 6;
    table.columns[5].displayedIndex = 7;
    table.columns[16].displayedIndex = 8;
    table.columns[35].displayedIndex = 9;

    table.columns[46].displayedIndex = 99999;   // Close button column
}

OrdersPanel.prototype.SetColumnsColouringMode = function (table)
{
    this.ProcessSetColumnsColouringMode(table, [8], ColouringModes.Previous);
    table.columnsIndexWithColoringByPrevValue = [8];
}
//​Symbol +
//Side  +
//Quantity +
//Current price +